/**
 * <s-bundle-offer> — keeps the bundle tier radios and the product quantity input in sync.
 *
 * Required markup:
 *   [data-quantity-input]  on the host: id of the product form's quantity <input>
 *   input[data-bundle-qty] radio per tier (1, 2, 3)
 * Choosing a tier writes its quantity into the input; typing or stepping the
 * quantity selects the matching tier (3 or more selects the top tier).
 */
class SBundleOffer extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('change', this.#handleTierChange, opts);

    const input = this.#quantityInput();
    if (input) {
      input.addEventListener('input', this.#handleQuantityChange, opts);
      input.addEventListener('change', this.#handleQuantityChange, opts);
      this.#selectFor(Number.parseInt(input.value, 10) || 1);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #quantityInput() {
    const id = this.dataset.quantityInput;
    const input = id ? document.getElementById(id) : null;
    return input instanceof HTMLInputElement ? input : null;
  }

  #handleTierChange = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || !radio.matches('[data-bundle-qty]')) return;
    const input = this.#quantityInput();
    if (!input) return;
    input.value = radio.value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  #handleQuantityChange = (event) => {
    if (event.target !== this.#quantityInput()) return;
    this.#selectFor(Number.parseInt(event.target.value, 10) || 1);
  };

  #selectFor(quantity) {
    const tier = Math.min(Math.max(quantity, 1), 3);
    const radio = this.querySelector(`[data-bundle-qty="${tier}"]`);
    if (radio instanceof HTMLInputElement && !radio.checked) radio.checked = true;
  }
}

if (!customElements.get('s-bundle-offer')) {
  customElements.define('s-bundle-offer', SBundleOffer);
}
